import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { replaceFileName } from '../../../util/path'

interface SubtitleCue {
  start: number
  end: number
  text: string
}

export const fetchSubtitles = async (
  videoUrl: string,
  lang: string
): Promise<string> => {
  const subtitlesUrl = replaceFileName(videoUrl, `subtitles_${lang}.vtt`)

  const response = await fetch(subtitlesUrl)

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`)
  }

  return await response.text()
}

const parseVTTString = (vtt: string): SubtitleCue[] => {
  const cues: SubtitleCue[] = []
  const lines = vtt.split('\n')

  let currentCue: SubtitleCue | null = null
  lines.forEach((line) => {
    line = line.trim()

    if (line.includes('WEBVTT')) {
      return
    }

    if (line.length === 0) {
      currentCue = null
      return
    }

    if (line.includes('-->')) {
      const [start, end] = line.split(' --> ')
      currentCue = {
        start: parseVTTTime(start),
        end: parseVTTTime(end),
        text: '',
      }
      cues.push(currentCue)
      return
    }

    if (currentCue) {
      currentCue.text += line + ' '
    }
  })

  return cues
}

const parseVTTTime = (timeString: string): number => {
  const parts = timeString.split(':')
  const hasHours = parts.length === 3
  if (hasHours) {
    const [hours, minutes, seconds] = parts
    const [sec, millis] = seconds.split('.')
    return (
      parseInt(hours, 10) * 3600 +
      parseInt(minutes, 10) * 60 +
      parseInt(sec, 10) +
      parseInt(millis, 10) / 1000
    )
  } else {
    const [minutes, seconds] = parts
    const [sec, millis] = seconds.split('.')
    return (
      parseInt(minutes, 10) * 60 +
      parseInt(sec, 10) +
      parseInt(millis, 10) / 1000
    )
  }
}

interface SubtitlesProps {
  subtitles: string
  progress: number
  duration: number
}

export const Subtitles = ({ subtitles, progress, duration }: SubtitlesProps) => {
  const [parsedCues, setParsedCues] = useState<SubtitleCue[]>([])
  const [currentSubtitle, setCurrentSubtitle] = useState<string>('')

  useEffect(() => {
    setParsedCues(parseVTTString(subtitles))
  }, [subtitles])

  useEffect(() => {
    const currentTime = (progress / 100) * duration
    const currentCue = parsedCues.find(
      (cue) => currentTime >= cue.start && currentTime <= cue.end
    )
    setCurrentSubtitle(currentCue?.text.trim() || '')
  }, [progress, duration, parsedCues])

  return (
    <SubtitlesContainer>
      {currentSubtitle && <Subtitle>{currentSubtitle}</Subtitle>}
    </SubtitlesContainer>
  )
}

const SubtitlesContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
`

const Subtitle = styled.span`
  background-color: rgba(0, 0, 0, 0.5);
  padding: 0.5em;
  color: white;
  font-size: 1.25em; 

  ${({ theme }) => theme.subtitlesCss}
`
